import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { Card } from "../Card";
import { StageBadge } from "./ConsentAttestationField";
import type { StageTone } from "./ConsentAttestationField";
import type { EngagementRecord } from "../../api/engagements";
import type { DrillStage, RenderStage } from "../../lib/videoDrill";
import { mapVideoError } from "../../lib/videoDrill";
import { ApiError } from "../../lib/api";
import { fetchRenderClipUrl } from "../../api/video";
import { useSubmitRender, useVideoRenderStatus } from "../../hooks/useVideo";

// ─── Button styles (local, consistent with VideoConfigCard) ───────────────────

const btnBase: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  borderRadius: "var(--radius-input)",
  fontFamily: "var(--font-mono)",
  fontSize: 10.5,
  letterSpacing: "0.14em",
  fontWeight: 600,
  cursor: "pointer",
  padding: "8px 14px",
  minHeight: 36,
  border: "1px solid transparent",
};

const primaryBtn: React.CSSProperties = {
  ...btnBase,
  background: "var(--accent)",
  color: "#FFFBF4",
  borderColor: "var(--accent)",
};

const primaryBtnDisabled: React.CSSProperties = {
  ...btnBase,
  background: "var(--bg-tertiary)",
  color: "var(--text-muted)",
  borderColor: "var(--border-subtle)",
  cursor: "not-allowed",
};

const ghostBtn: React.CSSProperties = {
  ...btnBase,
  background: "transparent",
  color: "var(--text-secondary)",
  borderColor: "var(--border)",
};

const ghostBtnDisabled: React.CSSProperties = {
  ...ghostBtn,
  color: "var(--text-muted)",
  borderColor: "var(--border-subtle)",
  cursor: "not-allowed",
};

// ─── Render stage → badge mapping ─────────────────────────────────────────────

interface StageMeta {
  label: string;
  tone: StageTone;
}

// StageTone only has muted | warning | success — there is no "accent" tone, so
// in-progress states use "warning" (amber) as the closest signal.
const RENDER_STAGE_META: Record<RenderStage, StageMeta> = {
  none: { label: "Not rendered", tone: "muted" },
  pending: { label: "Rendering…", tone: "warning" },
  ready: { label: "Clip ready", tone: "success" },
  stale: { label: "Clip out of date — re-render", tone: "warning" },
};

// ─── VideoRenderCard ──────────────────────────────────────────────────────────

interface Props {
  engagementId: string;
  rec: EngagementRecord;
  stage: DrillStage;
}

export function VideoRenderCard({ engagementId, rec, stage }: Props) {
  const qc = useQueryClient();
  // Poll while a render is PENDING — regardless of engagement state. You render
  // during DRAFT (before activation), so gating the poll on "active" left it
  // disabled on a draft and the card sat on "Rendering…" forever.
  const render = useVideoRenderStatus(engagementId, { live: stage.render === "pending" });

  // Once the poll reports completion, refresh the engagement record so the
  // stored clip (and is_video_clip_current) becomes visible.
  useEffect(() => {
    if (render.data?.status === "completed")
      void qc.invalidateQueries({ queryKey: ["engagement", engagementId] });
  }, [render.data?.status, engagementId, qc]);

  const submit = useSubmitRender(engagementId);
  const [err, setErr] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);

  function doRender() {
    setErr(null);
    submit.mutate(undefined, {
      onError: (e) =>
        setErr(e instanceof ApiError ? mapVideoError(e.detail) : "Render failed"),
    });
  }

  async function openPreview() {
    setErr(null);
    setPreviewing(true);
    try {
      const { url } = await fetchRenderClipUrl(engagementId);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setErr(e instanceof ApiError ? mapVideoError(e.detail) : "Couldn't open preview");
    } finally {
      setPreviewing(false);
    }
  }

  const stageMeta = RENDER_STAGE_META[stage.render];
  const notApproved = stage.script !== "approved";

  // The engagement record only flips to "completed"; HeyGen's live status
  // (processing / failed / …) comes from the poll. Surface it so a failed or
  // 503'd render is visible instead of an endless "Rendering…".
  const liveStatus = render.data?.status;
  const renderFailed = liveStatus === "failed" || liveStatus === "error";
  const pollErr =
    render.error instanceof ApiError
      ? mapVideoError(render.error.detail)
      : render.error
        ? "Render status couldn't be checked."
        : null;
  const isActivelyRendering = stage.render === "pending" && !renderFailed && !pollErr;

  const renderDisabled = notApproved || submit.isPending || isActivelyRendering;
  const btnLabel =
    submit.isPending || isActivelyRendering
      ? "RENDERING…"
      : stage.render === "ready" || renderFailed || pollErr
        ? "RE-RENDER"
        : "RENDER CLIP";

  const renderedAt = rec.video_render?.rendered_at ?? null;

  return (
    <Card title="" accentColor="var(--accent)">
      {/* Custom header row with title + badge */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingBottom: 12,
          borderBottom: "1px solid var(--border-subtle)",
          marginBottom: -2,
        }}
      >
        <h5
          style={{
            margin: 0,
            fontFamily: "var(--font-sans)",
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: "-0.005em",
            color: "var(--text-primary)",
          }}
        >
          Render
        </h5>
        <StageBadge label={stageMeta.label} tone={stageMeta.tone} />
      </div>

      {/* Body */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <p
          style={{
            margin: 0,
            fontSize: 11,
            color: "var(--text-muted)",
            fontFamily: "var(--font-sans)",
            lineHeight: 1.55,
            letterSpacing: "-0.005em",
          }}
        >
          The clip is rendered from the approved script and bound to the
          engagement.
        </p>

        {stage.render === "pending" ? (
          <div
            style={{
              fontSize: 11,
              fontFamily: "var(--font-mono)",
              color: renderFailed || pollErr ? "var(--error)" : "var(--text-secondary)",
              lineHeight: 1.5,
              letterSpacing: "0.02em",
            }}
          >
            {pollErr
              ? `Render status: ${pollErr}`
              : renderFailed
                ? `HeyGen render failed${render.data?.error ? `: ${render.data.error}` : ""} — fix the cause, then RE-RENDER.`
                : liveStatus
                  ? `HeyGen status: ${liveStatus} · checking every 5s…`
                  : "Submitting to HeyGen…"}
          </div>
        ) : null}

        {stage.render === "ready" && renderedAt ? (
          <div
            style={{
              fontSize: 11,
              color: "var(--text-secondary)",
              fontFamily: "var(--font-mono)",
              letterSpacing: "0.02em",
            }}
          >
            Rendered {new Date(renderedAt).toLocaleString()}
          </div>
        ) : null}

        {stage.render === "stale" ? (
          <div
            style={{
              fontSize: 11,
              color: "var(--warning)",
              fontFamily: "var(--font-sans)",
              lineHeight: 1.5,
            }}
          >
            The script changed since this clip was rendered — re-render before
            dispatching.
          </div>
        ) : null}

        {/* Error */}
        {err ? (
          <div style={{ color: "var(--error)", fontSize: 12, fontFamily: "var(--font-sans)" }}>
            {err}
          </div>
        ) : null}

        {/* Footer actions */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            paddingTop: 12,
            borderTop: "1px solid var(--border-subtle)",
            justifyContent: "flex-end",
          }}
        >
          {notApproved ? (
            <span
              style={{
                fontSize: 10.5,
                color: "var(--text-muted)",
                fontFamily: "var(--font-mono)",
                letterSpacing: "0.02em",
              }}
            >
              Approve the script first.
            </span>
          ) : null}
          {stage.render === "ready" ? (
            <button
              onClick={openPreview}
              disabled={previewing}
              style={previewing ? ghostBtnDisabled : ghostBtn}
              title="Open the rendered clip in a new tab"
            >
              {previewing ? "OPENING…" : "▶ PREVIEW CLIP"}
            </button>
          ) : null}
          <button
            onClick={doRender}
            disabled={renderDisabled}
            style={renderDisabled ? primaryBtnDisabled : primaryBtn}
          >
            {btnLabel}
          </button>
        </div>
      </div>
    </Card>
  );
}
